/**
 * Store paperwork tracker (ISD-575). Stores send CINTAS and Pest Control
 * invoices and licenses from The Sweet Spot; the photos land in each store's
 * Store Info files under Paperwork/<category>. This hook reads the per-store
 * monthly counts behind Stores > Setup > Paperwork.
 *
 * Backend: GET /v1/stores/hq/paperwork?period=YYYY-MM, gated by `stores.info`.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

// Category codes are data-driven: the api reads them from a DB table, so the
// set is open (today cintas, pest_control, licenses).
export type PaperworkCategory = string;

export type PaperworkOverview = {
  period: string;
  rules: { category: PaperworkCategory; label: string; need: number }[];
  stores: {
    store_no: string;
    store_name: string;
    counts: { category: PaperworkCategory; have: number; folder_id: number | null }[];
  }[];
};

export function usePaperworkOverview(period: string) {
  return useQuery<PaperworkOverview, Error>({
    queryKey: ['stores', 'paperwork', period],
    queryFn: () =>
      apiGet<PaperworkOverview>(`${API_BASE}/v1/stores/hq/paperwork?period=${encodeURIComponent(period)}`),
    staleTime: 30_000,
    retry: false,
  });
}

/** True when a store is short on any category that has a monthly count. */
export function isShort(
  counts: PaperworkOverview['stores'][number]['counts'],
  rules: PaperworkOverview['rules'],
): boolean {
  return rules.some((r) => r.need > 0 && (counts.find((c) => c.category === r.category)?.have ?? 0) < r.need);
}
